import { confirmDelivery } from "../../services/apis/dispense/confirmDelivery";

import { updateDeliveryStatus } from '../../services/apis/dispense/updateDeliveryStatus';
import { recordDriverActivitySignal } from "../../services/apis/dispense/routeAudit";
import { getPharmacyOrderDeliveryTime } from "./helper";
import { buildUnconsumedDosePayload, validateUnconsumedDoseEntries } from "./unconsumedDose";
import { buildDeliverRxIdsPayload, canSubmitRxForDelivery } from "./deliveredRxDisplay";
import { resolveDoseGapBlock } from "./doseGap";
import { requestDoseGapOverride } from "./doseGapPrompt";

const getSelectedDeliveryRxData = (rxData = [], selectedDeliverRxIds = []) => {
    if (!Array.isArray(rxData)) return [];
    if (!selectedDeliverRxIds?.length) return rxData.filter(canSubmitRxForDelivery);

    const selectedIdSet = new Set(selectedDeliverRxIds.map((id) => String(id)));
    return rxData.filter((rx) => selectedIdSet.has(String(rx?.id)) && canSubmitRxForDelivery(rx));
};

const isPrescriptionSignaturePending = (rx) => {
    const signatureData = rx?.pharmacyWinrxRxEs ?? rx?.pharmacy_winrx_rx_es;
    return signatureData?.signature_required === 1 && signatureData?.signature_complete === 0;
};

const isFentanylRx = (rx) => rx?.is_din_matched?.is_fentanyl === 1;

// Every "Yes" to returned patches needs how many came back: a whole number, 1 or
// more. Patches still owed from an earlier visit need All, Other (with a count up
// to what is owed) or None. Only the prescriptions being delivered are checked.
// The message to show, or '' when every count is fine.
const returnedPatchCountError = (isReturn = {}, rxIds = null) => {
    const ids = rxIds ? rxIds.map(String) : null;
    for (const [id, entry] of Object.entries(isReturn || {})) {
        if (ids && !ids.includes(String(id))) continue;
        const owed = Number(entry?.late_owed || 0);
        if (owed > 0) {
            if (!entry?.late_mode) return 'Still owed patches: choose All, Other or None.';
            if (entry.late_mode === 'other') {
                const late = String(entry?.late_qty ?? '').trim();
                if (!/^[1-9]\d*$/.test(late)) return 'Still owed patches: enter how many came back.';
                if (Number(late) > owed) return `Still owed patches: can't be more than ${owed}.`;
            }
        }
        if (entry?.answer !== 'yes') continue;
        const typed = String(entry?.returned_qty ?? '').trim();
        if (!/^[1-9]\d*$/.test(typed)) return 'Patches returned: choose All, or Partial and enter how many came back.';
        const expected = Number(entry?.expected_qty);
        if (entry?.expected_qty !== null && entry?.expected_qty !== undefined && Number.isFinite(expected) && Number(typed) > expected) {
            return `Returned patches can't be more than expected (${expected}).`;
        }
    }
    return '';
};

// -----------------------------------PENDING AND TODAY DISPENSE DELIVERY RX DATA START---------------
export const onhandleConfirmDelivery = (patientData,
    index,
    pharmacyId,
    selectePharmacist,
    patientSignature,
    pharmacistSignature,
    cordinate,
    comment,
    dispatch,
    setLoading,
    setOpenIndex,
    setShowErrorToast,
    setShowSuccessToast,
    pharmacyInspectionMode,
    isRefused,
    covid19,
    type,
    setIsRefused,
    setSignatureErrors,
    validateReturnedPatchesResponse,
    isTransferRequest,
    setIsTransferRequest,
    pharmacist_assistant_status,
    consultation,
    newRxCounsellingValues,
    selectedDoses,
    flexiProofImages = {},
    copayPaymentOptions = {},
    patientSerchData = null,
    isPatientVerified = false,
    selectedDeliverRxIds = [],
    unconsumedDoseEntries = {},
    setUnconsumedDoseErrors = () => { }
) => {

    const front_delivery_time = getPharmacyOrderDeliveryTime()
    const deliverRxPayload = buildDeliverRxIdsPayload(selectedDeliverRxIds, patientData?.patients_win_rx_data);
    const deliverRxIdsToSend = deliverRxPayload.deliver_rx_ids || selectedDeliverRxIds;
    // Get Flexi Delivery IDs (use merged RX for search; patientData.patients_win_rx_data is set by caller for search)
    const flexiDeliveryIDs = getFlexiDeliveryPatientIds(patientData?.patients_win_rx_data);
    // When user selected specific RXs, exclude flexi IDs whose parent RX is not selected
    const flexiDeliveryIDsToSend = selectedDeliverRxIds?.length > 0
        ? (flexiDeliveryIDs?.filter(id => deliverRxIdsToSend.includes(id)) || [])
        : flexiDeliveryIDs;
    // When user selected specific RXs, only include payment_options and flexi_proof_images for selected RXs
    const paymentOptionsToSend = selectedDeliverRxIds?.length > 0
        ? Object.entries(copayPaymentOptions || {}).filter(([winrxDataId]) => deliverRxIdsToSend.includes(Number(winrxDataId)) || deliverRxIdsToSend.includes(winrxDataId)).map(([, v]) => v).filter(option => option !== null)
        : Object.values(copayPaymentOptions || {}).filter(option => option !== null);

    const flexiProofImagesToSend = selectedDeliverRxIds?.length > 0
        ? Object.fromEntries(Object.entries(flexiProofImages || {}).filter(([, v]) => v?.id != null && deliverRxIdsToSend.includes(v.id)))
        : flexiProofImages;
    // When user selected specific RXs, only include counselling values for selected RXs
    const newRxCounsellingValuesToSend = selectedDeliverRxIds?.length > 0
        ? (newRxCounsellingValues?.filter((value) => value?.rxid != null && deliverRxIdsToSend.includes(value.rxid)) || [])
        : newRxCounsellingValues;

    const params = {
        route: patientData.route,
        pharmacy_id: pharmacyId,
        setLoading: setLoading,
    }

    const requesteData = {
        pharmacy_id: pharmacyId,
        pharmacist_id: selectePharmacist,
        patient_sign: [...patientSignature],
        pharmacist_sign: pharmacistSignature,
        delivery_latitude: cordinate?.latitude,
        delivery_longitude: cordinate?.longitude,
        patient_id: patientData?.id,
        comment: comment,
        dispatch: dispatch,
        params: params,
        setLoading: setLoading,
        setOpenIndex: setOpenIndex,
        setShowErrorToast: setShowErrorToast,
        setShowSuccessToast: setShowSuccessToast,
        type: type,
        is_refused: isRefused ? 1 : 0,
        covid19_exemptions: covid19 ? 1 : 0,
        is_return: validateReturnedPatchesResponse.isReturn,
        isTransferRequest: isTransferRequest,
        setIsTransferRequest: setIsTransferRequest,
        front_delivery_time: front_delivery_time,
        frequency_dose_id: Object?.values(selectedDoses)?.flat()?.map(dose => dose.dose_id),
        consultation: consultation,
        new_rx_counselling_values: newRxCounsellingValuesToSend,
        flexi_rx_refill_id: flexiDeliveryIDsToSend,
        hasFlexOnly: type,
        flexi_proof_images: flexiProofImagesToSend,
        payment_options: paymentOptionsToSend,
        is_patient_verified: Boolean(isPatientVerified),
        ...(type === 'patient_search_list' && patientSerchData ? { patientSerchData } : {}),
        ...deliverRxPayload,
        patientCardScrollIndex: index,
    }

    try {
        const rxDataForUnconsumedValidation = patientData?.patients_win_rx_data?.length > 0
            ? patientData.patients_win_rx_data
            : (patientData?.active_rx_data || []);

        if (!validateUnconsumedDoseEntries({
            rxData: rxDataForUnconsumedValidation,
            selectedDeliverRxIds: deliverRxIdsToSend,
            selectedDoses,
            unconsumedDoseEntries,
            setUnconsumedDoseErrors,
        })) {
            return;
        }

        const unconsumedDoses = buildUnconsumedDosePayload({
            rxData: rxDataForUnconsumedValidation,
            selectedDeliverRxIds: deliverRxIdsToSend,
            selectedDoses,
            unconsumedDoseEntries,
        });

        if (unconsumedDoses.length > 0) {
            requesteData.unconsumed_doses = unconsumedDoses;
        }

        const deliveryRuleRxData = getSelectedDeliveryRxData(patientData?.patients_win_rx_data, deliverRxIdsToSend);
        const hasRxWitness = deliveryRuleRxData.some(patient => patient.rx_witness === 'Y');
        const fentanylPatchesToCheck = deliveryRuleRxData.filter(isFentanylRx);
        const investageRx = deliveryRuleRxData.some(isPrescriptionSignaturePending)
        const signaturesRequired = pharmacyInspectionMode === 1 && ((isRefused ? null : patientSignature[0] == null))

        if (pharmacyId && patientData?.id) {
            const checkReturnedPatches = () => {
                if (validateReturnedPatchesResponse?.winrx_data_id?.length !== fentanylPatchesToCheck?.length) {
                    alert('Return Patches Fields are required.');
                } else if (returnedPatchCountError(validateReturnedPatchesResponse?.isReturn, fentanylPatchesToCheck.map((rx) => rx?.id))) {
                    alert(returnedPatchCountError(validateReturnedPatchesResponse?.isReturn, fentanylPatchesToCheck.map((rx) => rx?.id)));
                } else {
                    handleSignature(signaturesRequired);
                }
            }

            if (hasRxWitness) {
                if (patientSignature[0] == null && !covid19 && !isRefused) {
                    setSignatureErrors(prevState => ({ ...prevState, patientSign: '1px solid red' }))
                }
                else if (fentanylPatchesToCheck?.length) {
                    checkReturnedPatches()
                } else {
                    handleSignature(signaturesRequired);
                }
            }

            else if (fentanylPatchesToCheck?.length) {
                checkReturnedPatches()
            }

            else {
                handleSignature(signaturesRequired)
            }

        }

        function handleSignature(isSignatureRequired) {

            const patientSignError = ((isSignatureRequired || investageRx || isTransferRequest || newRxCounsellingValuesToSend?.length > 0) && patientSignature[0] === null);
            // const pharmacistSignError = pharmacist_assistant_status === 1 && pharmacistSignature === null;
            const hasNewRxValues = newRxCounsellingValuesToSend?.every((value) => value?.dropdownValue?.value)
            if (!hasNewRxValues) {
                return
            }
            else if (patientSignError && !isRefused) {
                setSignatureErrors(prevState => ({ ...prevState, patientSign: '1px solid red' }));
            }
            else {
                setSignatureErrors(prevState => ({ ...prevState, patientSign: null, pharmacistSign: null }));
                setIsRefused(false);

                const finalizeDelivery = () => {
                    if (type === 'patient_search_list') {
                        setTimeout(() => setShowSuccessToast(false), 2000);
                        setLoading(false);
                    }

                    recordDriverActivitySignal({
                        pharmacyId,
                        route: patientData?.route,
                    });
                    dispatch(confirmDelivery(requesteData));
                };

                // A dose the backend flagged as not due yet is confirmed before
                // anything is sent, so the driver is not surprised at the door.
                // The backend re-checks on submit either way.
                withDoseGapConfirmation(selectedDoses, requesteData, finalizeDelivery, setLoading, setShowErrorToast);
            }
        }

    } catch (error) {

    }
};

/**
 * Run a delivery, asking the driver to confirm first when the backend flagged a
 * selected dose as not due yet.
 *
 * The rule and the wording both live server-side; this only relays the prompt
 * and attaches the reason the driver gave.
 */
const withDoseGapConfirmation = (selectedDoses, requesteData, finalizeDelivery, setLoading, setShowErrorToast) => {
    const prompt = resolveDoseGapBlock(selectedDoses);

    if (!prompt) {
        finalizeDelivery();
        return;
    }

    requestDoseGapOverride(prompt).then((result) => {
        if (!result?.confirmed) {
            // Declined without the driver ever seeing the dialog stops the
            // delivery with nothing on screen, which reads as a dead button.
            // Say why instead.
            if (!result?.shown && typeof setShowErrorToast === 'function') {
                setShowErrorToast(prompt.message || 'This dose is not due yet.');
                setTimeout(() => setShowErrorToast(false), 4000);
            }
            if (typeof setLoading === 'function') setLoading(false);
            return;
        }

        requesteData.gap_override_confirmed = true;
        requesteData.gap_override_reason = result.reason;
        finalizeDelivery();
    });
};

export const getFlexiDeliveryPatientIds = (patientData) => {
    return patientData?.filter(patient => patient.flexi_delivery && canSubmitRxForDelivery(patient))
        ?.map(patient => patient.id) || [];
}

// -----------------------------------PENDING AND TODAY DISPENSE DELIVERY RX DATA END---------------


// -----------------------------------TODAY MISSED AND FINAL MISSED DISPENSE DELIVERY RX DATA START---------------
export const handleMissedConfirmDelivery = ({
    patientData,
    pharmacyId,
    selectedPharmacistID,
    patientSignature,
    pharmacistSignature,
    cordinate,
    comment,
    dispatch,
    setOpenIndex,
    setShowErrorToast,
    setShowSuccessToast,
    isRefused,
    covid19,
    validateReturnedPatchesResponse,
    isTransferRequest,
    setIsTransferRequest,
    pharmacyInspectionMode,
    setSignatureErrors,
    setIsRefused,
    fetchAction,
    hasRxWitness,
    consultation,
    new_rx_counselling_values,
    selectedDoses,
    flexiProofImages = {},
    copayPaymentOptions = {},
    isPatientVerified = false,
    selectedDeliverRxIds = [],
    unconsumedDoseEntries = {},
    setUnconsumedDoseErrors = () => { }
    // pharmacist_assistant_status
}) => {

    const front_delivery_time = getPharmacyOrderDeliveryTime()

    const rxDataForFetchAction = fetchAction === 'today_missed'
        ? (patientData?.patients_win_rx_data_today_missed || [])
        : fetchAction === 'final_missed'
            ? (patientData?.patients_win_rx_data_final_missed || [])
            : (patientData?.patients_win_rx_data || []);
    const deliverRxPayload = buildDeliverRxIdsPayload(selectedDeliverRxIds, rxDataForFetchAction);
    const deliverRxIdsToSend = deliverRxPayload.deliver_rx_ids || selectedDeliverRxIds;
    const flexiDeliveryIDs = getFlexiDeliveryPatientIds(rxDataForFetchAction);
    // When user selected specific RXs, exclude flexi IDs whose parent RX is not selected
    const flexiDeliveryIDsToSend = selectedDeliverRxIds?.length > 0
        ? (flexiDeliveryIDs?.filter(id => deliverRxIdsToSend.includes(id)) || [])
        : flexiDeliveryIDs;
    // When user selected specific RXs, only include payment_options and flexi_proof_images for selected RXs
    const paymentOptionsToSend = selectedDeliverRxIds?.length > 0
        ? Object.entries(copayPaymentOptions || {}).filter(([winrxDataId]) => deliverRxIdsToSend.includes(Number(winrxDataId)) || deliverRxIdsToSend.includes(winrxDataId)).map(([, v]) => v).filter(option => option !== null)
        : Object.values(copayPaymentOptions || {}).filter(option => option !== null);

    const flexiProofImagesToSend = selectedDeliverRxIds?.length > 0
        ? Object.fromEntries(Object.entries(flexiProofImages || {}).filter(([, v]) => v?.id != null && deliverRxIdsToSend.includes(v.id)))
        : flexiProofImages;
    // When user selected specific RXs, only include counselling values for selected RXs
    const newRxCounsellingValuesToSend = selectedDeliverRxIds?.length > 0
        ? (new_rx_counselling_values?.filter((value) => value?.rxid != null && deliverRxIdsToSend.includes(value.rxid)) || [])
        : new_rx_counselling_values;

    const params = {
        route: patientData?.route,
        pharmacy_id: pharmacyId,
    };

    const requesteData = {
        pharmacy_id: pharmacyId,
        pharmacist_id: selectedPharmacistID,
        patient_sign: [...patientSignature],
        pharmacist_sign: pharmacistSignature,
        delivery_latitude: cordinate?.latitude,
        delivery_longitude: cordinate?.longitude,
        patient_id: patientData?.id,
        comment: comment,
        dispatch: dispatch,
        params: params,
        setOpenIndex: setOpenIndex,
        setShowErrorToast: setShowErrorToast,
        setShowSuccessToast: setShowSuccessToast,
        type: fetchAction,
        is_refused: isRefused ? 1 : 0,
        covid19_exemptions: covid19 ? 1 : 0,
        is_return: validateReturnedPatchesResponse.isReturn,
        isTransferRequest,
        setIsTransferRequest,
        front_delivery_time: front_delivery_time,
        // frequency_dose_id: isDilaudidID[0]?.id,
        // frequency_dose_id: selectedDoses?.map(dose => dose?.dose_id),
        frequency_dose_id: Object?.values(selectedDoses)?.flat()?.map(dose => dose.dose_id),
        consultation: consultation,
        new_rx_counselling_values: newRxCounsellingValuesToSend,
        flexi_rx_refill_id: flexiDeliveryIDsToSend,
        flexi_proof_images: flexiProofImagesToSend,
        payment_options: paymentOptionsToSend,
        is_patient_verified: Boolean(isPatientVerified),
        ...deliverRxPayload
    };

    try {
        const rxDataForUnconsumedValidation = rxDataForFetchAction;

        if (!validateUnconsumedDoseEntries({
            rxData: rxDataForUnconsumedValidation,
            selectedDeliverRxIds: deliverRxIdsToSend,
            selectedDoses,
            unconsumedDoseEntries,
            setUnconsumedDoseErrors,
        })) {
            return;
        }

        const unconsumedDoses = buildUnconsumedDosePayload({
            rxData: rxDataForUnconsumedValidation,
            selectedDeliverRxIds: deliverRxIdsToSend,
            selectedDoses,
            unconsumedDoseEntries,
        });

        if (unconsumedDoses.length > 0) {
            requesteData.unconsumed_doses = unconsumedDoses;
        }

        if (pharmacyId && patientData?.id) {

            const deliveryRuleRxData = getSelectedDeliveryRxData(rxDataForUnconsumedValidation, deliverRxIdsToSend);
            const hasRxWitnessToCheck = selectedDeliverRxIds?.length > 0
                ? deliveryRuleRxData.some(patient => patient.rx_witness === 'Y')
                : (hasRxWitness ?? deliveryRuleRxData.some(patient => patient.rx_witness === 'Y'));
            const filteredDataToCheck = deliveryRuleRxData.filter(isFentanylRx);

            const checkReturnedPatches = () => {
                if (validateReturnedPatchesResponse?.winrx_data_id?.length !== filteredDataToCheck?.length) {
                    alert('Return Patches Fields are required.');
                } else if (returnedPatchCountError(validateReturnedPatchesResponse?.isReturn, filteredDataToCheck.map((rx) => rx?.id))) {
                    alert(returnedPatchCountError(validateReturnedPatchesResponse?.isReturn, filteredDataToCheck.map((rx) => rx?.id)));
                } else {
                    handleSignature(signaturesRequired);
                }
            }

            const signaturesRequired = pharmacyInspectionMode === 1 && (!isRefused && patientSignature[0] == null);
            const investageRx = deliveryRuleRxData.some(isPrescriptionSignaturePending)

            const handleSignature = () => {

                const patientSignError = ((signaturesRequired || investageRx || isTransferRequest || newRxCounsellingValuesToSend?.length > 0) && patientSignature[0] === null);
                // const pharmacistSignError = pharmacist_assistant_status === 1 && pharmacistSignature === null;
                const hasNewRxValues = newRxCounsellingValuesToSend?.every((value) => value?.dropdownValue?.value)
                if (!hasNewRxValues) {
                    return
                }
                else if (patientSignError && !isRefused) {
                    setSignatureErrors(prevState => ({ ...prevState, patientSign: '1px solid red' }));
                }
                else {
                    setSignatureErrors(prevState => ({ ...prevState, patientSign: null, pharmacistSign: null }));

                    const finalizeDelivery = () => {
                        recordDriverActivitySignal({
                            pharmacyId,
                            route: patientData?.route,
                        });
                        dispatch(confirmDelivery(requesteData));
                        setIsRefused(false);
                    };

                    // This path has no loading flag of its own to clear on cancel.
                    withDoseGapConfirmation(selectedDoses, requesteData, finalizeDelivery, null, setShowErrorToast);
                }
            };

            if (hasRxWitnessToCheck) {

                if (patientSignature[0] == null && !covid19 && !isRefused) {
                    setSignatureErrors(prevState => ({ ...prevState, patientSign: '1px solid red', pharmacistID: '1px solid red' }));
                }

                else if (filteredDataToCheck?.length) {
                    checkReturnedPatches()
                }

                else {
                    handleSignature();
                }
            }

            else if (filteredDataToCheck?.length) {
                checkReturnedPatches()
            }

            else {
                handleSignature();
            }
        }
    }
    catch (error) {
        console.error("Error confirming delivery:", error);
    }
};
// -----------------------------------TODAY MISSED AND FINAL MISSED DISPENSE DELIVERY RX DATA END---------------






// -----------------------------------PENDING AND TODAY DISPENSE MISSED RX DATA END---------------
export const onhandleMissedDelivery = (pharmacyId,
    missedData,
    missedIndex,
    dispatch,
    setOpenIndex,
    setMissedDeliveryErrorToast,
    setMissedDeliveryConfirmation,
    missedExplanation,
    setMissedExplanation,
    setMissedExplanationError,
    status,
    type,
    selectedDoses,
    selectedDeliverRxIds = [],
    cordinate = null
) => {

    // const isDilaudidID = missedData?.patients_win_rx_data
    //     ?.map((value) => value?.investigate_dilaudid_delivery)
    //     .filter(id => id !== null).flat()

    // Get Flexi Delivery IDs
    const flexiDeliveryIDs = getFlexiDeliveryPatientIds(missedData?.patients_win_rx_data);
    const deliverRxPayload = buildDeliverRxIdsPayload(selectedDeliverRxIds, missedData?.patients_win_rx_data);
    const deliverRxIdsToSend = deliverRxPayload.deliver_rx_ids || selectedDeliverRxIds;
    // When user selected specific RXs, exclude flexi IDs whose parent RX is not selected
    const flexiDeliveryIDsToSend = selectedDeliverRxIds?.length > 0
        ? (flexiDeliveryIDs?.filter(id => deliverRxIdsToSend.includes(id)) || [])
        : flexiDeliveryIDs;

    const params = {
        pharmacy_id: pharmacyId,
        route: missedData.route,
        status: status
    };

    const requesteData = {
        pharmacy_id: pharmacyId,
        dispatch: dispatch,
        params: params,
        patient_id: missedData?.id,
        setOpenIndex: setOpenIndex,
        type: type,
        setMissedDeliveryErrorToast: setMissedDeliveryErrorToast,
        setMissedDeliveryConfirmation: setMissedDeliveryConfirmation,
        // frequency_dose_id: isDilaudidID[0]?.id
        // frequency_dose_id: selectedDoses?.map(dose => dose?.dose_id),
        frequency_dose_id: Object?.values(selectedDoses)?.flat()?.map(dose => dose.dose_id),
        missed_explanation: missedExplanation?.trim(),
        delivery_latitude: cordinate?.latitude ?? null,
        delivery_longitude: cordinate?.longitude ?? null,
        flexi_rx_refill_id: flexiDeliveryIDsToSend,
        ...deliverRxPayload,
        missedPatientListIndex: missedIndex,
        setMissedExplanation,
    };

    try {
        if (missedData?.id && pharmacyId) {
            if (!missedExplanation?.trim()) {
                setMissedExplanationError('Explanation is required.');
                return;
            }

            setMissedExplanationError('');

            recordDriverActivitySignal({
                pharmacyId,
                route: missedData?.route,
            });
            dispatch(updateDeliveryStatus(requesteData));
        }
    } catch (error) {
        // Handle error
    }
};
// -----------------------------------PENDING AND TODAY DISPENSE DELIVERY RX DATA END---------------

export const flexiNotificationRxNumbers = (flexiMarkedNotificationsResponse,
    allPatientsRxData,
    patient_id) => {

    const flexiRxData = flexiMarkedNotificationsResponse
        ?.filter(value => value?.patient_id === patient_id
            && value?.flexi_delivery_status === true)

    // Completed and handed-over rows remain visible but must not open the flexi
    // confirmation flow for the current driver.
    const matchedFlexiData = allPatientsRxData?.filter(patientRx =>
        canSubmitRxForDelivery(patientRx)
        && flexiRxData?.some(flexiItem => flexiItem.refill_id === patientRx.id)
    );

    return matchedFlexiData?.map(item => item.rxnumber);
}

